import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import api, { apiErr, fmtTime, fmtTimeLong } from "../../lib/api";
import { Card, SectionTitle, Alert, btnPrimary, btnSecondary, CostBadge } from "../ui-bits";
import VoicePickerButton from "../VoicePickerButton";
import { toast } from "sonner";
import { Mic, Play, Pause, Loader2, Clock, RefreshCw } from "lucide-react";

export default function TabNarracion({ project, config, refreshProject, setTab }) {
  const { t } = useTranslation();
  const [voice, setVoice] = useState(project?.narration?.voice || "");
  const [busy, setBusy] = useState(project?.narration?.status === "generando");
  const [genSegId, setGenSegId] = useState(null);
  const [playingId, setPlayingId] = useState(null);
  const [costSeconds, setCostSeconds] = useState(0);
  const audioRef = useRef(null);
  const pollRef = useRef(null);

  const segments = project?.segments || [];
  const done = project?.narration?.status === "completo";

  // Recargo real de narrar (kind="narration_premium", ai33 — ver synth_segment) — antes no se
  // mostraba ningún coste aquí, se cobraba en silencio al pulsar "Generar narración". Se
  // recalcula al cambiar de voz.
  useEffect(() => {
    if (!voice || !project?.id) { setCostSeconds(0); return; }
    let cancel = false;
    api.post("/cost/estimate", { action: "narration", project_id: project.id, voice })
      .then(({ data }) => { if (!cancel) setCostSeconds(data?.seconds || 0); })
      .catch(() => { if (!cancel) setCostSeconds(0); });
    return () => { cancel = true; };
  }, [voice, project?.id]);

  // Polling mientras se genera la narración en segundo plano
  useEffect(() => {
    if (!busy) return;
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/projects/${project.id}`);
        await refreshProject();
        if (data.narration?.status === "completo") {
          clearInterval(pollRef.current); setBusy(false);
          toast.success(t("narr.toastDone", { time: fmtTimeLong(data.narration.total_duration) }));
        } else if (data.narration?.status === "error") {
          clearInterval(pollRef.current); setBusy(false);
          toast.error(data.narration.error || t("narr.toastErr"));
        }
      } catch (e) { /* sigue intentando */ }
    }, 2500);
    return () => clearInterval(pollRef.current);
  }, [busy]); // eslint-disable-line

  if (!segments.length) {
    return <Alert type="warning" testid="narration-no-segments">{t("narr.noSegments")}</Alert>;
  }

  const playScene = (s) => {
    if (!s.audio_url) return;
    const url = `${process.env.REACT_APP_BACKEND_URL}${s.audio_url}`;
    if (playingId === s.id && audioRef.current) {
      audioRef.current.pause(); setPlayingId(null); return;
    }
    if (audioRef.current) audioRef.current.pause();
    const a = new Audio(url);
    audioRef.current = a;
    a.onended = () => setPlayingId(null);
    a.onerror = () => { toast.error(t("narr.playError")); setPlayingId(null); };
    a.play().then(() => setPlayingId(s.id)).catch(() => setPlayingId(null));
  };

  const generate = async () => {
    if (!voice) { toast.warning(t("narr.toastChooseVoice")); return; }
    setBusy(true);
    try {
      await api.post(`/projects/${project.id}/narration`, { voice });
      toast.info(t("narr.toastGenAll"));
    } catch (e) { toast.error(apiErr(e)); setBusy(false); }
  };

  const generateSegment = async (s) => {
    if (!voice) { toast.warning(t("narr.toastChooseVoice")); return; }
    setGenSegId(s.id);
    try {
      await api.post(`/projects/${project.id}/segments/${s.id}/narration`, { voice });
      await refreshProject();
      toast.success(t("narr.toastGenScene", { num: s.num }));
    } catch (e) { toast.error(apiErr(e)); } finally { setGenSegId(null); }
  };

  // costSeconds llega en 0 cuando todas las escenas ya están narradas con esta voz (sin coste
  // NUEVO esta vez, pero la voz sigue siendo de pago) — mostrar "Gratis" ahí sería engañoso
  // (bug real reportado). Con ai33 como única vía, cualquier voz elegida es de pago: basta con
  // saber si hay una elegida, ya no hace falta buscarla en una lista para comprobarlo.
  const isPremiumSelected = !!voice;

  return (
    <div className="space-y-8">
      <SectionTitle sub={t("narr.subtitle")}>{t("narr.title")}</SectionTitle>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-6 space-y-6">
          <div>
            <label className="block text-base font-semibold text-slate-800 mb-3">
              {t("narr.voiceLabel")}
            </label>
            <VoicePickerButton language={project?.language || "es"} value={voice} onChange={(v) => setVoice(v.id)} />
          </div>

          <button data-testid="generate-narration-button" onClick={generate} disabled={busy || !voice} className={`w-full ${btnPrimary} disabled:opacity-60`}>
            {busy ? <Loader2 className="animate-spin" size={22} /> : <Mic size={22} />} {busy ? t("narr.generating") : t("narr.generateAll")}
            {!busy && voice && (
              <CostBadge dark seconds={costSeconds} free={costSeconds === 0}
                freeLabel={isPremiumSelected ? t("narr.noExtraCost") : t("narr.free")}
                tip={t("narr.costTip")} />
            )}
          </button>
        </Card>

        <Card className="p-6">
          <h3 className="text-xl font-bold font-head text-slate-900 mb-4">{t("narr.perSceneStatus")}</h3>
          <div className="overflow-hidden border-2 border-slate-200 rounded-lg">
            <table className="w-full text-base">
              <thead className="bg-slate-50">
                <tr className="text-left text-slate-600">
                  <th className="px-4 py-3 font-semibold">{t("narr.colScene")}</th>
                  <th className="px-4 py-3 font-semibold">{t("narr.colDuration")}</th>
                  <th className="px-4 py-3 font-semibold">{t("narr.colStatus")}</th>
                  <th className="px-4 py-3 font-semibold text-right">{t("narr.colAction")}</th>
                </tr>
              </thead>
              <tbody data-testid="narration-table">
                {segments.map((s) => (
                  <tr key={s.id} className="border-t border-slate-200">
                    <td className="px-4 py-3 font-medium text-slate-900">
                      <div className="flex items-center gap-2">
                        {s.narration_status === "completo" && s.audio_url && (
                          <button data-testid={`play-scene-${s.id}`} onClick={() => playScene(s)} className="text-blue-600 hover:text-blue-800 p-1 rounded-full hover:bg-blue-50 shrink-0" title={t("narr.listenNarration")}>
                            {playingId === s.id ? <Pause size={18} /> : <Play size={18} />}
                          </button>
                        )}
                        <span>{t("narr.scene", { num: s.num, title: s.title })}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-700"><Clock size={16} className="inline mr-1 text-slate-400" />{fmtTime(s.narration_duration || s.duration)}</td>
                    <td className="px-4 py-3">
                      {s.narration_status === "completo"
                        ? <span className="text-emerald-600 font-semibold">{t("narr.complete")}</span>
                        : s.narration_status === "error"
                        ? <span className="text-red-600 font-semibold">{t("narr.failedStatus")}</span>
                        : (s.narration_status === "generando" || busy || genSegId === s.id) ? <span className="text-blue-600 font-semibold flex items-center gap-1"><Loader2 className="animate-spin" size={14} /> {t("narr.genStatus")}</span>
                        : <span className="text-slate-500">{t("narr.pending")}</span>}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button data-testid={`generate-scene-${s.id}`} onClick={() => generateSegment(s)} disabled={busy || genSegId === s.id || !voice}
                        className="inline-flex items-center gap-1.5 text-sm font-bold text-blue-700 hover:text-blue-900 disabled:opacity-40">
                        {genSegId === s.id ? <Loader2 className="animate-spin" size={16} /> : (s.narration_status === "completo" ? <RefreshCw size={16} /> : <Mic size={16} />)}
                        {s.narration_status === "completo" ? t("narr.regenerate") : t("narr.generate")}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {done && (
            <div className="mt-4 space-y-3">
              <Alert type="success">{t("narr.doneAlert", { time: fmtTimeLong(project.narration.total_duration) })}</Alert>
              <button onClick={() => setTab("broll")} className={`w-full ${btnSecondary}`}>{t("narr.continue")}</button>
            </div>
          )}
        </Card>
      </div>

    </div>
  );
}
